/**
 * AdminFieldVerificationPanel — PRD1.7 Step 4B
 * Admin UI for per-field verification of clinic profile data.
 * Shows claimed values, linked documents, verification status.
 * Actions: Verify, Reject, Link Document.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CheckCircle2, XCircle, Clock, Shield, FileText, Link2,
  Loader2, AlertTriangle, ChevronDown, ChevronUp, Building2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from '../../hooks/use-toast';

const STATUS_STYLES = {
  verified: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircle2, label: 'Verified' },
  pending: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: Clock, label: 'Pending' },
  rejected: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
  unverified: { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', icon: AlertTriangle, label: 'Unverified' },
};

const FieldStatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unverified;
  const Icon = s.icon;
  return (
    <Badge className={`${s.bg} ${s.text} border text-xs`} data-testid={`field-status-${status}`}>
      <Icon className="h-3 w-3 mr-1" /> {s.label}
    </Badge>
  );
};

const FIELD_LABELS = {
  founding_year: 'Founding Year',
  doctors_count: 'Doctors Count',
  years_of_experience: 'Years of Experience',
  procedures_performed: 'Total Procedures Performed',
  international_patient_volume: 'International Patient Volume',
};

export default function AdminFieldVerificationPanel({ clinicId, adminToken }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectFor, setShowRejectFor] = useState(null);
  const [expandedDocs, setExpandedDocs] = useState(false);

  const headers = {};

  const fetchData = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/clinic-trust/${clinicId}/verified-fields`, { headers });
      setData(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load verification data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (fieldName, action, accreditationName = null, sourceDocumentId = null) => {
    const key = accreditationName || fieldName;
    setActionLoading(key);
    try {
      const body = {
        field_name: fieldName,
        action,
        accreditation_name: accreditationName || undefined,
        rejection_reason: action === 'reject' ? rejectReason : undefined,
      };

      // Explicit source document (canonical accreditation-evidence flow) wins.
      if (sourceDocumentId) {
        body.source_document_id = sourceDocumentId;
      } else {
        // Fallback: legacy field-linked document.
        const linkedDoc = (data?.linked_documents || []).find(d => d.linked_field === fieldName);
        if (linkedDoc) body.source_document_id = linkedDoc.document_id;
      }

      await api.post(`/admin/clinic-trust/${clinicId}/verify-field`, body, { headers });
      toast({ title: action === 'verify' ? 'Verified' : 'Rejected', description: `${FIELD_LABELS[fieldName] || fieldName}${accreditationName ? ` (${accreditationName})` : ''} has been ${action}ed.` });
      setShowRejectFor(null);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Action failed', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleLinkDocument = async (documentId, fieldName) => {
    setActionLoading(`link-${documentId}`);
    try {
      await api.post(`/admin/clinic-trust/${clinicId}/link-document`, {
        document_id: documentId,
        linked_field: fieldName,
      }, { headers });
      toast({ title: 'Linked', description: `Document linked to ${FIELD_LABELS[fieldName] || fieldName}` });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed to link', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 py-4 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading verification data...</div>
  );

  if (!data) return (
    <div className="text-gray-500 text-sm py-4">No verification data available for this clinic.</div>
  );

  const { verified_fields: vf, claimed_values: claimed, linked_documents: docs } = data;
  const accEvidence = data.accreditation_documents || [];
  const scalarFields = ['founding_year', 'doctors_count', 'years_of_experience', 'procedures_performed', 'international_patient_volume'];

  return (
    <div className="space-y-4" data-testid="field-verification-panel">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="h-5 w-5 text-blue-600" />
        <h3 className="font-semibold text-gray-800">Field-Level Verification</h3>
        <span className="text-xs text-gray-400">Clinic: {data.clinic_name}</span>
      </div>

      {/* Scalar Fields */}
      {scalarFields.map(field => {
        const claimedValue = claimed[field];
        const fieldData = vf[field] || {};
        const status = fieldData.status || 'unverified';
        const linkedDoc = (docs || []).find(d => d.linked_field === field);

        if (claimedValue == null && status === 'unverified') return null;

        return (
          <Card key={field} className={`border ${STATUS_STYLES[status]?.bg || ''}`} data-testid={`field-card-${field}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-800">{FIELD_LABELS[field] || field}</p>
                  <p className="text-lg font-bold text-gray-900">{claimedValue != null ? claimedValue : 'Not provided'}</p>
                </div>
                <FieldStatusBadge status={status} />
              </div>

              {linkedDoc && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <FileText className="h-3 w-3" />
                  Linked: {linkedDoc.document_id} ({linkedDoc.document_type || 'unknown'})
                </div>
              )}

              {/* Actions */}
              {status !== 'verified' && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    onClick={() => handleAction(field, 'verify')}
                    disabled={actionLoading === field}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`verify-${field}`}
                  >
                    {actionLoading === field ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                    Verify
                  </Button>
                  {showRejectFor === field ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        placeholder="Rejection reason"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        className="h-8 text-xs"
                        data-testid={`reject-reason-${field}`}
                      />
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleAction(field, 'reject')}
                        disabled={actionLoading === field}
                        data-testid={`confirm-reject-${field}`}
                      >
                        Reject
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setShowRejectFor(null); setRejectReason(''); }}>Cancel</Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600"
                      onClick={() => setShowRejectFor(field)}
                      data-testid={`reject-btn-${field}`}
                    >
                      <XCircle className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Accreditations (per-item) */}
      {(claimed.accreditations || []).length > 0 && (
        <Card data-testid="accreditations-verification">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-600" />
              Accreditations ({(claimed.accreditations || []).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(claimed.accreditations || []).map((accName, i) => {
              const accVf = (vf.accreditations || []).find(a => a.name === accName) || {};
              const status = accVf.status || 'unverified';

              return (
                <div key={accName || i} className={`p-3 rounded-lg border ${STATUS_STYLES[status]?.bg || 'bg-white'}`} data-testid={`acc-verify-${i}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{accName}</p>
                    </div>
                    <FieldStatusBadge status={status} />
                  </div>

                  {status !== 'verified' && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleAction('accreditations', 'verify', accName)}
                        disabled={actionLoading === accName}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`verify-acc-${i}`}
                      >
                        {actionLoading === accName ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Verify
                      </Button>
                      {showRejectFor === `acc-${accName}` ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            placeholder="Reason"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            className="h-8 text-xs"
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleAction('accreditations', 'reject', accName)}
                            disabled={actionLoading === accName}
                          >
                            Reject
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowRejectFor(null); setRejectReason(''); }}>Cancel</Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => setShowRejectFor(`acc-${accName}`)}
                          data-testid={`reject-acc-${i}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Accreditation Evidence — canonical, document-bound (TREATX-PROVIDER-ACCRED-CANONICAL-01).
          Each row is an uploaded certificate already bound to a canonical
          accreditation. Approve calls the SAME verify-field authority with the
          document's slug + id — no manual mapping, no free-text matching. */}
      {accEvidence.length > 0 && (
        <Card data-testid="accreditation-evidence">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              Accreditation Evidence ({accEvidence.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {accEvidence.map((doc, i) => {
              const status = doc.verification_status || 'unverified';
              const rejectKey = `accdoc-${doc.document_id}`;
              return (
                <div key={doc.document_id || i} className={`p-3 rounded-lg border ${STATUS_STYLES[status]?.bg || 'bg-white'}`} data-testid={`acc-evidence-row-${doc.accreditation_slug}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{doc.accreditation_label}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-2">
                        <FileText className="h-3 w-3" />
                        {doc.file_url
                          ? <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" data-testid={`acc-evidence-file-${doc.accreditation_slug}`}>{doc.file_name || 'View certificate'}</a>
                          : (doc.file_name || 'No file')}
                        {doc.expiry_date ? ` · valid until ${doc.expiry_date}` : ''}
                      </p>
                    </div>
                    <FieldStatusBadge status={status} />
                  </div>

                  {status !== 'verified' && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                      <Button
                        size="sm"
                        onClick={() => handleAction('accreditations', 'verify', doc.accreditation_slug, doc.document_id)}
                        disabled={actionLoading === doc.accreditation_slug}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-acc-evidence-${doc.accreditation_slug}`}
                      >
                        {actionLoading === doc.accreditation_slug ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                        Approve
                      </Button>
                      {showRejectFor === rejectKey ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            placeholder="Reason"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            className="h-8 text-xs"
                            data-testid={`reject-acc-evidence-reason-${doc.accreditation_slug}`}
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleAction('accreditations', 'reject', doc.accreditation_slug, doc.document_id)}
                            disabled={actionLoading === doc.accreditation_slug}
                            data-testid={`confirm-reject-acc-evidence-${doc.accreditation_slug}`}
                          >
                            Reject
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowRejectFor(null); setRejectReason(''); }}>Cancel</Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => setShowRejectFor(rejectKey)}
                          data-testid={`reject-acc-evidence-btn-${doc.accreditation_slug}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Linked Documents Overview */}
      {(docs || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedDocs(!expandedDocs)}>
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                Linked Documents ({docs.length})
              </span>
              {expandedDocs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
          </CardHeader>
          {expandedDocs && (
            <CardContent className="space-y-2">
              {docs.map((doc, i) => (
                <div key={doc.document_id || i} className="flex items-center gap-3 text-sm p-2 bg-gray-50 rounded">
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{doc.document_id}</p>
                    <p className="text-xs text-gray-500">{doc.document_type || 'unknown'} — linked to: {FIELD_LABELS[doc.linked_field] || doc.linked_field || 'none'}</p>
                  </div>
                  <Link2 className="h-4 w-4 text-blue-400 shrink-0" />
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}

      {/* Unlinked Documents — allow admin to link */}
      {(() => {
        const unlinked = (docs || []).filter(d => !d.linked_field);
        // Also look for all verification documents without linked_field
        return null; // This is handled via link-document endpoint when admin uses the UI
      })()}
    </div>
  );
}
